import React, { useMemo } from 'react';
import { View, ScrollView, StyleSheet, Platform } from 'react-native';
import type { ViewStyle } from 'react-native';
import checkIsEven from 'src/core/utils/checkIsEven';
import GalleryTile from 'src/presentation/components/molecules/GalleryTile';
import Title from 'src/presentation/components/atoms/Title';
import Divider from 'src/presentation/components/atoms/Divider';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type { ApiGallery } from 'src/core/models/api/ApiGallery';

interface GalleryGridProps {
	galleries?: ApiGallery[];
	filtered?: boolean;
	onGalleryTilePress?: (tile: ApiGallery) => void;
}

function GalleryGrid({ galleries, filtered, onGalleryTilePress }: GalleryGridProps) {
	const contentWidth = useContentWidth();

	const sizedStyles = useMemo(
		(): { galleryGridIos: ViewStyle } => ({
			galleryGridIos: {
				width: contentWidth,
			},
		}),
		[contentWidth],
	);

	if (!galleries || galleries.length === 0) {
		if (filtered) {
			return <EmptyState icon="search-outline" message={Translations.textNoResultsFound} />;
		}
		return (
			<EmptyState
				icon="images-outline"
				message={Translations.textNoPhotosMessage}
				title={Translations.textNoPhotosTitle}
			/>
		);
	}

	// iOS: één flex-wrap grid.
	if (Platform.OS === 'ios') {
		const gallerytiles = galleries.map((gallerytile) => (
			<GalleryTile
				galleries={gallerytile.children}
				galleryholder={gallerytile}
				key={gallerytile.id}
				onGalleryTilePress={onGalleryTilePress}
			/>
		));

		return (
			<View style={styles.wrapContainer}>
				<View style={[styles.galleryGridIos, sizedStyles.galleryGridIos]}>{gallerytiles}</View>
			</View>
		);
	}

	// Android: gegroepeerd op categorie met horizontale scroll per groep.
	function renderAndroidTiles(galleriesData: ApiGallery[]) {
		return galleriesData.map((gallerytile, index) => (
			<GalleryTile
				galleries={gallerytile.children}
				galleryholder={gallerytile}
				key={gallerytile.id}
				spaceAround={{
					marginRight: checkIsEven(index) ? undefined : Dimens.margin16,
				}}
				onGalleryTilePress={onGalleryTilePress}
			/>
		));
	}

	const newArray: Record<string, ApiGallery[]> = {};
	galleries.forEach((gallery) => {
		const category = gallery.category;
		const bucket = newArray[category] ?? [];
		bucket.push(gallery);
		newArray[category] = bucket;
	});

	const entries = Object.entries(newArray);
	const arrayLength = entries.length;
	const renderGalleryGrid = entries.map(([category, gallery], idx) => {
		const count = idx + 1;
		return (
			<View key={category}>
				<View style={styles.title}>
					<Title text={category + ' evenementen'} />
				</View>
				<ScrollView
					contentContainerStyle={styles.androidScrollContent}
					horizontal={true}
					showsHorizontalScrollIndicator={false}
				>
					{renderAndroidTiles(gallery)}
				</ScrollView>
				{count === arrayLength ? null : <Divider />}
			</View>
		);
	});

	return <View>{renderGalleryGrid}</View>;
}

export default GalleryGrid;

const styles = StyleSheet.create({
	wrapContainer: {
		alignItems: 'center',
	},
	galleryGridIos: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		marginTop: Dimens.margin25,
		paddingLeft: Dimens.padding30,
		paddingRight: Dimens.padding30,
		paddingBottom: Dimens.padding20 * 2,
		gap: 25,
	},
	title: {},
	androidScrollContent: {
		marginLeft: Dimens.margin16,
		paddingRight: Dimens.padding16,
		marginBottom: Dimens.margin15,
	},
});
